/**
 * useDailyIncome — B1.16 / Phase 1 MVP
 *
 * Ruft GET /api/v2/dashboard/daily_income ab für Chart-Daten.
 * S10-17: Support for from/to parameters for custom timespan.
 */
import { useApi } from '../../lib/use-api'
import apiClient from '../../lib/api-client'
import { subDays, format } from 'date-fns'
import { tagScopeKey, tagScopeQuery } from './dashboardScope'

export type Period = '7d' | '30d' | '90d'

type DailyIncomeEntry = {
  date: string
  hnt: number
  iot: number
  mobile: number
}

type UseDailyIncomeResult = {
  data: DailyIncomeEntry[] | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export function getPeriodDates(period: Period): { from: string; to: string } {
  const now = new Date()
  const days = period === '7d' ? 7 : period === '30d' ? 30 : 90
  return {
    from: format(subDays(now, days), 'yyyy-MM-dd'),
    to: format(now, 'yyyy-MM-dd'),
  }
}

type UseDailyIncomeOptions = {
  period: Period
  from?: string
  to?: string
  /** R5/Stream TD: Chart im Tag-Scope (`?tag=<id>` -> tag_id). */
  tagId?: number | null
}

/**
 * Hook für tägliche Income-Daten für den Chart.
 *
 * Ruft GET /api/v2/dashboard/daily_income ab.
 * S10-17: from/to params for custom timespan override period-based dates.
 */
export function useDailyIncome(options: UseDailyIncomeOptions): UseDailyIncomeResult {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const client = apiClient as any

  // S10-17: Support custom from/to or fall back to period-based dates
  const from = options.from || getPeriodDates(options.period).from
  const to = options.to || getPeriodDates(options.period).to
  const tagId = options.tagId ?? null

  const query = useApi<DailyIncomeEntry[]>(
    ['dashboard', 'daily_income', from, to, tagScopeKey(tagId)],
    async () => {
      const result = await client.GET('/api/v2/dashboard/daily_income', {
        params: {
          query: {
            from,
            to,
            group_by: 'day',
            ...tagScopeQuery(tagId),
          },
        },
      })
      return {
        data: result?.data as DailyIncomeEntry[] | undefined,
        error: result?.error,
      }
    },
  )

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  }
}
